import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import {
  LocalStorageManagerService,
  STARTUP_HELPER_PROMPT_KEY,
} from '../storage/local-storage-manager.service';
import {
  isStartupHelperSetUp,
  shouldPromptStartupHelper,
  startupHelperDownloadedMarker,
  startupHelperSetUpMarker,
} from '../utils/startup-helper';

/**
 * The Windows auto-start helper's persisted state (`jio_startup_helper_prompt`, see
 * startup-helper.ts), behind one signal so every surface reacts at once — the title-bar button
 * stays mounted for the whole session and must disappear the moment a warm `setup-complete`
 * link arrives, not on the next launch.
 *
 * States, strongest first:
 * - `setup:<v>` — the helper reported success (StartupHelperLaunchService). Card, header and
 *   title-bar buttons, and the install-screen link are hidden; Profile keeps a "set up again"
 *   link as the one deliberate way back (e.g. after Chrome was reinstalled).
 * - `downloaded:<v>` — the user clicked a download. Silences the card only: the header and
 *   title-bar buttons stay, because a download doesn't mean the exe was run, and those buttons
 *   are how a user who lost or skipped the file gets it again.
 * - epoch-ms — last "Not now" on the card (7-day cooldown).
 *
 * A download never downgrades `setup:` back to `downloaded:` (the Profile re-run would otherwise
 * resurrect every prompt). Storage is plain localStorage, so this is readable before
 * `hydrate()` — the launch handler runs ahead of the bootstrap gates.
 *
 * Other windows on this origin share the key: the `setup-complete` link lands in the installed
 * app, while a browser tab may sit on the install screen with the helper link showing. A
 * `storage` event (fired only in the *other* windows) drops the cache so that tab updates live.
 */
@Injectable({ providedIn: 'root' })
export class StartupHelperStateService {
  private readonly storage = inject(LocalStorageManagerService);

  // Read lazily, on first use rather than at construction: the shell and the self-gating
  // surfaces inject this everywhere, and most never look (wrong platform, switch off).
  private cached: string | null | undefined;
  private readonly writes = signal(0);
  private stored(): string | null {
    this.writes();
    if (this.cached === undefined) this.cached = this.storage.getStartupHelperPrompt();
    return this.cached;
  }

  constructor() {
    if (typeof window === 'undefined') return;
    const onStorage = (event: StorageEvent) => {
      // `key === null` is a `localStorage.clear()` elsewhere.
      if (event.key !== null && event.key !== STARTUP_HELPER_PROMPT_KEY) return;
      this.cached = undefined;
      this.writes.update((n) => n + 1);
    };
    window.addEventListener('storage', onStorage);
    inject(DestroyRef).onDestroy(() => window.removeEventListener('storage', onStorage));
  }

  /** The helper is set up for the current `requiredVersion`. */
  readonly isSetUp = computed(() => isStartupHelperSetUp(this.stored()));

  /** Whether the first-run card should ask now (see `shouldPromptStartupHelper`). */
  shouldPrompt(now: number = Date.now()): boolean {
    return shouldPromptStartupHelper(this.stored(), now);
  }

  markSetUp(): void {
    this.write(startupHelperSetUpMarker());
  }

  /** Download from any surface. Keeps `setup:` if already set up (see class doc). */
  recordDownload(): void {
    if (this.isSetUp()) return;
    this.write(startupHelperDownloadedMarker());
  }

  recordNotNow(now: number = Date.now()): void {
    this.write(String(now));
  }

  private write(value: string): void {
    this.storage.setStartupHelperPrompt(value);
    this.cached = value;
    this.writes.update((n) => n + 1);
  }
}
